import React from "react";
import { Drawer, Button, Checkbox, Row, Col, Card, message } from "antd";
import groupBy from "lodash/groupBy";

const AddOnSelectionDrawer = ({
  displayAddOnSelection,
  layout,
  handleAddOnModalClose,
  selectedProduct,
  currenciesList,
  addOnsList,
  addonQty,
  setAddonQty,
  validateAddonSelection,
}) => {
  // --- helper functions (operate on addOnsList + addonQty) ---

  const getGroupItems = (groupName) => {
    if (!addOnsList?.requiredList) return [];
    return addOnsList.requiredList.filter((addon) => (addon.mAddonGroup && addon.mAddonGroup.name) === groupName);
  };

  const getGroupTotalQty = (groupName) => {
    const items = getGroupItems(groupName);
    return items.reduce((t, item) => t + (Number(addonQty[item.mProductAddOnId]) || 0), 0);
  };

  const increaseAddonQty = (id, groupName) => {
    if (!addOnsList?.requiredList) return;
    const groupItems = getGroupItems(groupName);
    if (!groupItems.length) {
      console.warn("increaseAddonQty: groupItems empty for", groupName);
    }

    const maxqty = Number(groupItems[0]?.mAddonGroup?.maxqty) || Infinity;
    const total = getGroupTotalQty(groupName);

    if (total >= maxqty) {
      message.error(`You can select only ${maxqty} items for ${groupName}`);
      return;
    }

    setAddonQty((prev) => {
      const next = { ...prev, [id]: (Number(prev[id]) || 0) + 1 };
      return next;
    });
  };

  const decreaseAddonQty = (id) => {
    setAddonQty((prev) => {
      const current = Number(prev[id]) || 0;
      const nextVal = Math.max(current - 1, 0);
      const next = { ...prev, [id]: nextVal };
      return next;
    });
  };

  // --- render ---

  return (
    <Drawer
      placement="bottom"
      height="75vh"
      closable={false}
      maskClosable={true}
      styles={{ mask: { backgroundColor: "rgba(0, 0, 0, 0.3)" } }}
      mask={true}
      onClose={handleAddOnModalClose}
      open={layout !== "2" && displayAddOnSelection}
      styles={{ header: { display: "none" } }}
      styles={{ body: { padding: 0, background: "#fff", display: "flex", flexDirection: "column", height: "100%" } }}
      style={{ borderRadius: "12px 12px 0 0" }}
    >
      {/* PRODUCT HEADER */}
      <div style={{ padding: "15px" }}>
        <span style={{ fontSize: "1.3em", fontWeight: 500 }}>{selectedProduct?.name}</span>

        <span style={{ fontSize: "1.3em", fontWeight: 500, float: "right" }}>
          {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
          {selectedProduct?.sunitprice}
          {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol}` : ""}
        </span>

        <p style={{ color: "#8f8f8f", marginTop: 5 }}>{selectedProduct?.description}</p>
      </div>

      {/* ADD-ON GROUPS */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 15px" }}>
        {addOnsList?.requiredList?.length > 0 &&
          Object.entries(groupBy(addOnsList.requiredList, (a) => a?.mAddonGroup?.name || "Others")).map(([groupName, items]) => {
            const min = Number(items[0]?.mAddonGroup?.minqty || 0);
            const max = Number(items[0]?.mAddonGroup?.maxqty || Infinity);

            return (
              <div key={groupName} style={{ marginBottom: 18 }}>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>
                  {groupName}
                  {min > 0 && <span style={{ color: "red" }}> *</span>}

                  <span style={{ float: "right", fontSize: "0.9em" }}>
                    {getGroupTotalQty(groupName)} / {max === Infinity ? "No Max Limit" : max}
                  </span>
                </div>

                {items.map((item) => {
                  const allowQty = item.mAddonGroup?.allowQtySelection === "Y";

                  return (
                    <Card
                      key={item.mProductAddOnId}
                      style={{
                        marginBottom: 10,
                        borderRadius: 5,
                        border: "1px solid #eee",
                      }}
                      styles={{ body: { padding: 12 } }}
                    >
                      <Row justify="space-between" align="middle">
                        {/* LEFT SIDE -- checkbox + name */}
                        <Col span={12} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {/* Checkbox ALWAYS shown */}
                          <Checkbox
                            checked={addonQty[item.mProductAddOnId] > 0}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const groupName = item.mAddonGroup?.name;
                              const groupItems = getGroupItems(groupName);
                              const max = Number(item.mAddonGroup?.maxqty || Infinity);
                              const allowQty = item.mAddonGroup?.allowQtySelection === "Y";

                              const currentTotalQty = groupItems.reduce((t, g) => t + (Number(addonQty[g.mProductAddOnId]) || 0), 0);

                              if (allowQty) {
                                if (checked && currentTotalQty + 1 > max) {
                                  message.error(`You can select only ${max} items in ${groupName}`);
                                  return;
                                }

                                setAddonQty((prev) => ({
                                  ...prev,
                                  [item.mProductAddOnId]: checked ? 1 : 0,
                                }));
                                return;
                              }

                              const selectedCount = groupItems.reduce((t, g) => t + (addonQty[g.mProductAddOnId] > 0 ? 1 : 0), 0);

                              if (checked && selectedCount >= max) {
                                message.error(`You can select only ${max} items in ${groupName}`);
                                return;
                              }

                              setAddonQty((prev) => ({
                                ...prev,
                                [item.mProductAddOnId]: checked ? 1 : 0,
                              }));
                            }}
                          />

                          <span style={{ fontWeight: 500 }}>{item.name}</span>
                        </Col>

                        {/* RIGHT SIDE -- qty controls ONLY when allowQtySelection = Y */}
                        <Col
                          span={12}
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {allowQty ? (
                            <>
                              <Button size="small" onClick={() => decreaseAddonQty(item.mProductAddOnId)}>
                                -
                              </Button>

                              <span>{addonQty[item.mProductAddOnId] || 0}</span>

                              <Button size="small" onClick={() => increaseAddonQty(item.mProductAddOnId, item.mAddonGroup?.name)}>
                                +
                              </Button>
                            </>
                          ) : null}

                          {item.price > 0 && (
                            <span style={{ fontWeight: 500, marginLeft: 10 }}>
                              {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                              {item.price}
                              {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol}` : ""}
                            </span>
                          )}
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </div>
            );
          })}
      </div>

      {/* ADD TO CART BUTTON */}
      <div
        style={{
          padding: 15,
          borderTop: "1px solid #ddd",
          background: "#fff",
          position: "sticky",
          bottom: 0,
        }}
      >
        <Button block size="large" type="primary" style={{ fontSize: "1.2em", height: 45, backgroundColor: "#2f3856" }} onClick={validateAddonSelection}>
          Add to Cart
        </Button>
      </div>
    </Drawer>
  );
};

export default AddOnSelectionDrawer;
